/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/no-array-index-key */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { UiHtml } from '@powdr/components';
import { ColorProfiles, LinkTargets } from '@powdr/constants';
import { getMinSelectableDate } from '@powdr/utils';

import * as TabHooks from './hooks';
import {
  StyledBookingWidget,
  TabExecuteButton,
  StyledSelect,
  StyledDatePicker,
  FieldLabel,
  FieldWrapper,
  WidgetFields,
} from './styles';

export const BookingWidget = ({
  title,
  tabType,
  copy,
  promoCode,
  submitButtonText,
  submitButtonTarget,
  commerceEngineUrl,
  selectableItemType,
  lodgingDepartureOffest,
  relationships: {
    dropdownOptions,
  },
  isInSidebarDrawer = false,
  colorProfile = ColorProfiles.BASE,
  is5050 = false,
  maxBookableDate = null,
  minBookableDate = null,
}) => {
  const useTabHook = () => {
    try {
      return TabHooks[`use${tabType}`]({
        commerceEngineUrl,
        dropdownOptions,
        selectableItemType,
        lodgingDepartureOffest,
        title,
        promoCode,
      });
    } catch {
      console.error('Tab Type in Booking Widget does not exist, reverting to fallback!');

      return TabHooks.useSingleDate({
        commerceEngineUrl,
        dropdownOptions,
        selectableItemType,
        lodgingDepartureOffest,
        title,
        promoCode,
      });
    }
  };

  const {
    setTabValues, tabValues = {}, buttonLink = '', components: hookComponents,
  } = useTabHook();
  const components = hookComponents.map((c) => (
    (c.type === 'datepicker' && (maxBookableDate || minBookableDate))
      ? { ...c, maxBookableDate, minBookableDate }
      : c
  ));

  const handleDateChange = (name, minDate, selectedDate) => {
    setTabValues({
      [name]: {
        minDate,
        selectedDate,
      },
    });
  };

  const handleSelectChange = (selected, name) => {
    setTabValues({ [name]: { ...selected } });
  };

  const componentFields = components.map((component, componentIndex) => {
    const componentKey = `tab-${title}-${componentIndex}`;
    const commonFieldProps = { setTabValues, tabValues };
    const getField = () => {
      switch (component.type) {
        case 'datepicker':
          return (
            <StyledDatePicker
              key={componentKey}
              id={componentKey}
              ariaLabel={component.ariaLabel}
              ariaLabelledBy={`${componentKey}-label`}
              placeholder={component.placeholder}
              prettyDate={component.prettyDate}
              dateFormat={component.dateFormat}
              isDefaultDate={component.isDefaultDate}
              isAutoSelected={tabValues[component.name]?.isAutoSelected}
              maxNumDays={component.maxDaysToBookAhead}
              maxBookableDate={component.maxBookableDate}
              minBookableDate={component.minBookableDate}
              colorProfile={colorProfile}
              useColorProfile={false}
              isCurrentDaySelectable
              handleDateChange={(date) => handleDateChange(
                component.name,
                getMinSelectableDate({
                  minBookableDate: component.minBookableDate,
                  minDate: tabValues[component.name]?.minDate,
                  isCurrentDaySelectable: true,
                }),
                date,
              )}
              minDate={tabValues[component.name]?.minDate}
              is5050={is5050}
            />
          );

        case 'select':
          return (
            <StyledSelect
              key={componentKey}
              id={componentKey}
              colorProfile={ColorProfiles.BASE}
              ariaLabel={component.ariaLabel}
              ariaLabelledBy={`${componentKey}-label`}
              component={component}
              {...commonFieldProps}
              handleSelectChange={handleSelectChange}
              is5050={is5050}
              menuPlacement={isInSidebarDrawer ? 'top' : 'bottom'}
            />
          );

        case 'radio': {
          return (
            <StyledSelect
              key={componentKey}
              colorProfile={ColorProfiles.BASE}
              id={componentKey}
              ariaLabel={component.ariaLabel}
              ariaLabelledBy={`${componentKey}-label`}
              radio
              component={component}
              choices={component.choices}
              {...commonFieldProps}
              handleSelectChange={handleSelectChange}
              is5050={is5050}
            />
          );
        }

        default:
          return null;
      }
    };
    // TODO: Destructure component vals
    return (
      <FieldWrapper>
        {(!component?.isHideLabel) && (
          <FieldLabel
            id={`${componentKey}-label`}
            aria-label={component.ariaLabel}
            $colorProfile={colorProfile}
          >
            {component?.label || component.placeholder}
          </FieldLabel>
        )}
        {getField()}
      </FieldWrapper>
    );
  });

  return (
    <StyledBookingWidget $is5050={is5050} $isInSidebarDrawer={isInSidebarDrawer}>
      {(copy?.value) && <UiHtml html={copy} parentColorProfile={colorProfile} />}
      <WidgetFields $is5050={is5050} $isInSidebarDrawer={isInSidebarDrawer}>
        {componentFields}
        <TabExecuteButton
          className={classNames({ disabled: !buttonLink })}
          href={buttonLink}
          target={submitButtonTarget || LinkTargets.BLANK}
          onClick={(e) => { if (!buttonLink) e.preventDefault(); }}
          role="button"
          $colorProfile={colorProfile}
        >
          {submitButtonText}
        </TabExecuteButton>
      </WidgetFields>
    </StyledBookingWidget>
  );
};

BookingWidget.propTypes = {
  title: PropTypes.string.isRequired,
  tabType: PropTypes.string.isRequired,
  submitButtonText: PropTypes.string.isRequired,
  submitButtonTarget: PropTypes.string,
  commerceEngineUrl: PropTypes.string,
  selectableItemType: PropTypes.string,
  copy: PropTypes.string,
  lodgingDepartureOffest: PropTypes.number,
  promoCode: PropTypes.string,
  relationships: PropTypes.shape().isRequired,
  colorProfile: PropTypes.string,
  is5050: PropTypes.bool,
  isInSidebarDrawer: PropTypes.bool,
  maxBookableDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  minBookableDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};
